import {RequestInfo} from "@khan/shared-core";

import {getConsentCookieCategories} from "./get-consent-cookie-categories.ts";
import {CookieCategory} from "./types.ts";

/**
 * Create a cookie.
 *
 * @param name - The name of the cookie.
 * @param cookieCategory - The category of the cookie.
 * @param [value] - The value to set for the cookie.
 * @param [days] - The number of days until the cookie expires.
 * @param [domain] - The domain for which the cookie is valid.
 * @param [isSecure] - Whether the cookie should be marked as secure.
 * @param [sameSite] - The SameSite attribute for the cookie. Defaults to "Lax".
 *
 * @returns True if the cookie was successfully created, false otherwise.
 */
export function createCookie(
    name: string,
    cookieCategory: CookieCategory,
    value?: string | number | boolean | null,
    days?: number,
    domain?: string,
    isSecure?: boolean,
    // By default, the SameSite attribute is set to Lax because we only
    // want to support our cookies in a first-party context, as well as
    // when third parties redirect to our site.
    sameSite: "None" | "Lax" | "Strict" = "Lax",
): boolean {
    if (
        // Strictly necessary cookies can always be created
        cookieCategory &&
        cookieCategory !== CookieCategory.StrictlyNecessary &&
        // We also allow cookies in any category to be created in dev
        !RequestInfo.IS_DEV_SERVER
    ) {
        const acceptedCookieGroups = getConsentCookieCategories();
        // if the user has not accepted the cookie category, do not set the cookie
        if (!acceptedCookieGroups.get(cookieCategory)) {
            return false;
        }
    }
    let expires: string;
    if (days) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = "; expires=" + date.toUTCString();
    } else {
        expires = "";
    }
    const domainPart = domain ? `; domain=${domain}` : "";
    const secure = isSecure ? "; secure" : "";
    document.cookie =
        name +
        "=" +
        String(value) +
        expires +
        domainPart +
        secure +
        "; SameSite=" +
        sameSite +
        "; path=/";
    return true;
}
